import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  Sparkles, X as XIcon, Save, CheckCircle, AlertCircle,
  Send, Info, Wand2, ChevronDown, ChevronUp, Eye,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemedText, Button } from '../';
import { aiFormSchemaService } from '../../api';
import FieldDiffView from './FieldDiffView';

// ─── Layout ────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${p => p.theme.spacing.lg};
`;

const ModalContent = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  border: 1px solid ${p => p.theme.colors.border};
  max-width: 580px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const ModalHeader = styled.div`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${p => p.theme.colors.backgroundSecondary};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const HeaderIconBadge = styled.div`
  width: 34px;
  height: 34px;
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.tint}18;
  border: 1px solid ${p => p.theme.colors.tint}30;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.tint};
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  font-size: ${p => p.theme.typography.sizes.default};
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  line-height: 1.3;
`;

const HeaderMeta = styled.div`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${p => p.theme.colors.border};
    color: ${p => p.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: ${p => p.theme.spacing.lg};
`;

const ModalFooter = styled.div`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-top: 1px solid ${p => p.theme.colors.border};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  background: ${p => p.theme.colors.backgroundSecondary};
`;

// ─── Initial State ─────────────────────────────────────────────────────────

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const ModeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 2px solid ${p => p.$active ? p.theme.colors.tint : p.theme.colors.border};
  background: ${p => p.$active ? `${p.theme.colors.tint}08` : p.theme.colors.background};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
  width: 100%;

  &:hover {
    border-color: ${p => p.$active ? p.theme.colors.tint : `${p.theme.colors.tint}70`};
    background: ${p => p.$active ? `${p.theme.colors.tint}08` : `${p.theme.colors.tint}04`};
  }
`;

const ModeCardIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.$active ? `${p.theme.colors.tint}20` : p.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.muted};
  margin-bottom: 2px;
`;

const ModeCardTitle = styled.div`
  font-size: ${p => p.theme.typography.sizes.default};
  font-weight: 600;
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.text};
`;

const ModeCardDesc = styled.div`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  line-height: 1.4;
`;

const RunButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background: linear-gradient(135deg, ${p => p.theme.colors.tint}, ${p => p.theme.colors.tint}cc);
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.sizes.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px ${p => p.theme.colors.tint}40;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px ${p => p.theme.colors.tint}55;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// ─── Loading ───────────────────────────────────────────────────────────────

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.xl} 0;
`;

const Spinner = styled.div`
  border: 3px solid ${p => p.theme.colors.border};
  border-top: 3px solid ${p => p.theme.colors.tint};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 0.9s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ButtonSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  animation: spin 0.9s linear infinite;
  flex-shrink: 0;
`;

// ─── Results ───────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: ${p => p.theme.spacing.xs};
`;

const FeedbackCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-left: 3px solid ${p => p.theme.colors.tint};
  margin-bottom: ${p => p.theme.spacing.md};
  overflow: hidden;
`;

const FeedbackCardToggle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.small};
  font-weight: 600;
  gap: ${p => p.theme.spacing.sm};
`;

const FeedbackCardBody = styled.div`
  padding: 0 ${p => p.theme.spacing.md} ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.typography.sizes.default};
  color: ${p => p.theme.colors.text};
  line-height: 1.6;
  border-top: 1px solid ${p => p.theme.colors.border};
  padding-top: ${p => p.theme.spacing.sm};
`;

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p =>
    p.$variant === 'success' ? '#bbf7d0' :
    p.$variant === 'info'    ? '#bfdbfe' : p.theme.colors.border};
  background: ${p =>
    p.$variant === 'success' ? '#f0fdf4' :
    p.$variant === 'info'    ? '#eff6ff' : p.theme.colors.backgroundSecondary};
  color: ${p =>
    p.$variant === 'success' ? '#166534' :
    p.$variant === 'info'    ? '#1e40af' : p.theme.colors.text};
  font-weight: 600;
  font-size: ${p => p.theme.typography.sizes.default};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const SuggestionsList = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-size: ${p => p.theme.typography.sizes.default};
  color: ${p => p.theme.colors.text};
  line-height: 1.5;
  background: ${p => p.theme.colors.background};

  &:last-child { border-bottom: none; }
`;

const SuggestionIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${p =>
    p.$type === 'error'   ? '#ef4444' :
    p.$type === 'warning' ? '#f59e0b' : p.theme.colors.tint};
`;

// ─── Chat ──────────────────────────────────────────────────────────────────

const ChatSection = styled.div`
  border-top: 1px solid ${p => p.theme.colors.border};
  padding-top: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.md};
`;

const ChatHistory = styled.div`
  max-height: 160px;
  overflow-y: auto;
  margin-bottom: ${p => p.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  padding-right: 2px;
`;

const ChatBubble = styled.div`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
  line-height: 1.5;
  background: ${p => p.$role === 'user'
    ? `${p.theme.colors.tint}12`
    : p.theme.colors.backgroundSecondary};
  border-left: 3px solid ${p => p.$role === 'user'
    ? p.theme.colors.tint
    : p.theme.colors.border};
`;

const ChatBubbleLabel = styled.span`
  display: block;
  font-weight: 700;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.55;
  margin-bottom: 2px;
`;

const ChatInputRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const ChatTextArea = styled.textarea`
  flex: 1;
  padding: ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  font-family: inherit;
  resize: none;
  min-height: 56px;
  line-height: 1.5;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.tint}18;
  }

  &::placeholder { color: ${p => p.theme.colors.muted}; }
  &:disabled { opacity: 0.6; }
`;

const SendHint = styled.div`
  font-size: 10px;
  color: ${p => p.theme.colors.muted};
  margin-top: 4px;
  text-align: right;
`;

// ─── Prompts ───────────────────────────────────────────────────────────────

const EVAL_PROMPT = 'Evaluate this form for quality, completeness, and usability. Identify any missing important fields, unclear labels, or structural issues. Suggest specific improvements.';

const POLISH_PROMPT = 'Polish all existing fields in this form: improve field titles for clarity, improve placeholder text and helper text to be more user-friendly and professional. Do not add or remove any fields and do not change field keys or field types. Return the same set of fields with improved titles, labels, and helper text only.';

// ─── Component ─────────────────────────────────────────────────────────────

export const PreSaveEvaluationModal = ({
  isOpen,
  onClose,
  onSave,
  onApply,
  onApplyAndSave,
  currentSchema,
  formName,
  fieldCount,
}) => {
  const theme = useTheme();
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [evaluationContext, setEvaluationContext] = useState([]);
  const [polishMode, setPolishMode] = useState(false);
  const [feedbackExpanded, setFeedbackExpanded] = useState(true);

  if (!isOpen) return null;

  const sanitizeSchema = (schema) => {
    if (!schema?.fields) return schema;
    return {
      ...schema,
      fields: schema.fields.map(f => ({
        ...f,
        title: f.title || 'Untitled Field',
        key: f.key || 'unnamed_field',
      })),
    };
  };

  const activePrompt = polishMode ? POLISH_PROMPT : EVAL_PROMPT;

  const handleEvaluate = async () => {
    setIsEvaluating(true);
    setEvaluationResult(null);
    setConversationHistory([]);
    setFeedbackExpanded(true);

    try {
      const result = await aiFormSchemaService.refineSchema({
        currentSchema,
        message: activePrompt,
        conversationHistory: [],
      });

      if (result.success) {
        setEvaluationResult(result);
        setEvaluationContext([
          { role: 'user', content: activePrompt },
          { role: 'assistant', content: result.reasoning || 'Evaluation complete.' },
        ]);
        setConversationHistory([]);
      } else {
        toast.error(result.errorMessage || 'Evaluation failed');
      }
    } catch (error) {
      console.error('AI evaluation failed:', error);
      toast.error('Evaluation failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleRefine = async () => {
    if (!chatMessage.trim() || !evaluationResult?.schemaDefinition) return;

    setIsRefining(true);
    const userMsg = chatMessage.trim();
    setChatMessage('');

    const updatedDisplayHistory = [...conversationHistory, { role: 'user', content: userMsg }];
    setConversationHistory(updatedDisplayHistory);
    const apiHistory = [...evaluationContext, ...updatedDisplayHistory];

    try {
      const result = await aiFormSchemaService.refineSchema({
        currentSchema: sanitizeSchema(evaluationResult.schemaDefinition),
        message: userMsg,
        conversationHistory: apiHistory.slice(-6),
      });

      if (result.success) {
        setEvaluationResult(result);
        setConversationHistory(prev => [
          ...prev,
          { role: 'assistant', content: result.reasoning || 'Schema updated.' },
        ]);
        toast.success('Schema refined!');
      } else {
        toast.error(result.errorMessage || 'Failed to refine schema');
        setConversationHistory(updatedDisplayHistory.slice(0, -1));
      }
    } catch (error) {
      console.error('Failed to refine schema:', error);
      toast.error('Failed to refine: ' + (error.message || 'Unknown error'));
      setConversationHistory(updatedDisplayHistory.slice(0, -1));
    } finally {
      setIsRefining(false);
    }
  };

  const handleClose = () => {
    setIsEvaluating(false);
    setEvaluationResult(null);
    setChatMessage('');
    setIsRefining(false);
    setConversationHistory([]);
    setEvaluationContext([]);
    setPolishMode(false);
    setFeedbackExpanded(true);
    onClose();
  };

  // Sanitize the schema before passing to parent — fixes "Title field is required" save error
  const handleApplyAndSave = () => {
    onApplyAndSave({
      ...evaluationResult,
      schemaDefinition: sanitizeSchema(evaluationResult.schemaDefinition),
    });
  };

  // Apply AI changes to form without saving, then close modal
  const handleApply = () => {
    onApply({
      ...evaluationResult,
      schemaDefinition: sanitizeSchema(evaluationResult.schemaDefinition),
    });
    // Reset modal state so reopening starts fresh
    setEvaluationResult(null);
    setConversationHistory([]);
    setChatMessage('');
    onClose();
  };

  const hasSuggestions = evaluationResult?.validationResult && (
    (evaluationResult.validationResult.suggestions?.length > 0) ||
    (evaluationResult.validationResult.warnings?.length > 0) ||
    (evaluationResult.validationResult.errors?.length > 0)
  );

  const originalKeys = new Set((currentSchema?.fields || []).map(f => f.key));
  const resultKeys = new Set((evaluationResult?.schemaDefinition?.fields || []).map(f => f.key));
  const schemaWasModified = !!evaluationResult && (
    resultKeys.size !== originalKeys.size ||
    [...resultKeys].some(k => !originalKeys.has(k)) ||
    [...originalKeys].some(k => !resultKeys.has(k))
  );
  const hasActionableResult = hasSuggestions || schemaWasModified || (polishMode && !!evaluationResult);

  const allSuggestions = evaluationResult ? [
    ...(evaluationResult.validationResult?.errors || []).map(e => ({ text: e.message || e, type: 'error' })),
    ...(evaluationResult.validationResult?.warnings || []).map(w => ({ text: w.message || w, type: 'warning' })),
    ...(evaluationResult.validationResult?.suggestions || []).map(s => ({ text: s, type: 'info' })),
  ] : [];

  return (
    <ModalOverlay theme={theme}>
      <ModalContent theme={theme} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <ModalHeader theme={theme}>
          <HeaderLeft theme={theme}>
            <HeaderIconBadge theme={theme}>
              <Save size={15} />
            </HeaderIconBadge>
            <div>
              <HeaderTitle theme={theme}>Save Form</HeaderTitle>
              {(formName || fieldCount > 0) && (
                <HeaderMeta theme={theme}>
                  {formName && <strong>{formName}</strong>}
                  {formName && fieldCount > 0 && ' · '}
                  {fieldCount > 0 && `${fieldCount} field${fieldCount !== 1 ? 's' : ''}`}
                </HeaderMeta>
              )}
            </div>
          </HeaderLeft>
          <CloseButton theme={theme} onClick={handleClose}>
            <XIcon size={16} />
          </CloseButton>
        </ModalHeader>

        <ModalBody theme={theme}>

          {/* ── Initial state ── */}
          {!isEvaluating && !evaluationResult && (
            <>
              <SectionLabel theme={theme} style={{ marginBottom: theme.spacing.sm }}>AI Review Mode</SectionLabel>
              <ModeGrid theme={theme}>
                <ModeCard theme={theme} $active={!polishMode} onClick={() => setPolishMode(false)}>
                  <ModeCardIcon theme={theme} $active={!polishMode}>
                    <Sparkles size={14} />
                  </ModeCardIcon>
                  <ModeCardTitle theme={theme} $active={!polishMode}>Evaluate & Suggest</ModeCardTitle>
                  <ModeCardDesc theme={theme}>Review completeness and suggest new fields or structural improvements</ModeCardDesc>
                </ModeCard>
                <ModeCard theme={theme} $active={polishMode} onClick={() => setPolishMode(true)}>
                  <ModeCardIcon theme={theme} $active={polishMode}>
                    <Wand2 size={14} />
                  </ModeCardIcon>
                  <ModeCardTitle theme={theme} $active={polishMode}>Polish Fields</ModeCardTitle>
                  <ModeCardDesc theme={theme}>Improve titles, labels & hints on existing fields only — no structural changes</ModeCardDesc>
                </ModeCard>
              </ModeGrid>
              <RunButton theme={theme} onClick={handleEvaluate}>
                {polishMode ? <Wand2 size={15} /> : <Sparkles size={15} />}
                {polishMode ? 'Polish with AI' : 'Evaluate with AI'}
              </RunButton>
            </>
          )}

          {/* ── Loading ── */}
          {isEvaluating && (
            <LoadingSection theme={theme}>
              <Spinner theme={theme} />
              <ThemedText style={{ color: theme.colors.muted }}>
                {polishMode ? 'Polishing your fields…' : 'Evaluating your form…'}
              </ThemedText>
            </LoadingSection>
          )}

          {/* ── Results ── */}
          {evaluationResult && (
            <div>
              {/* Suggestions / Status — shown first for visibility */}
              {hasSuggestions && allSuggestions.length > 0 ? (
                <div style={{ marginBottom: theme.spacing.md }}>
                  <SectionLabel theme={theme}>Suggestions ({allSuggestions.length})</SectionLabel>
                  <SuggestionsList theme={theme}>
                    {allSuggestions.map((item, i) => (
                      <SuggestionItem key={i} theme={theme}>
                        <SuggestionIcon theme={theme} $type={item.type}>
                          {item.type === 'error'
                            ? <AlertCircle size={14} />
                            : item.type === 'warning'
                            ? <AlertCircle size={14} />
                            : <Info size={14} />}
                        </SuggestionIcon>
                        {item.text}
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                </div>
              ) : hasActionableResult ? (
                <StatusBanner theme={theme} $variant="info">
                  {polishMode ? <Wand2 size={16} /> : <Sparkles size={16} />}
                  {polishMode
                    ? 'AI polished your field labels and descriptions.'
                    : 'AI made improvements to your schema.'}
                </StatusBanner>
              ) : (
                <StatusBanner theme={theme} $variant="success">
                  <CheckCircle size={16} />
                  Looks great! No issues found.
                </StatusBanner>
              )}

              {/* Metadata changes (form name, description, icon) */}
              {evaluationResult?.schemaDefinition && (
                (evaluationResult.schemaDefinition.title && evaluationResult.schemaDefinition.title !== currentSchema?.title) ||
                (evaluationResult.schemaDefinition.description && evaluationResult.schemaDefinition.description !== currentSchema?.description) ||
                (evaluationResult.schemaDefinition.icon && evaluationResult.schemaDefinition.icon !== currentSchema?.icon)
              ) && (
                <div style={{
                  padding: theme.spacing.sm,
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.border}`,
                  background: theme.colors.backgroundSecondary,
                  marginTop: theme.spacing.sm,
                  fontSize: theme.typography.sizes.small,
                }}>
                  <div style={{ fontWeight: theme.typography.weights.semiBold, marginBottom: theme.spacing.xs, color: theme.colors.muted }}>
                    Metadata Changes
                  </div>
                  {evaluationResult.schemaDefinition.title && evaluationResult.schemaDefinition.title !== currentSchema?.title && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing.xs, lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 600, color: theme.colors.muted, minWidth: 80 }}>Name:</span>
                      <span style={{ textDecoration: 'line-through', color: theme.colors.danger }}>{currentSchema?.title || '(empty)'}</span>
                      <span style={{ color: theme.colors.muted }}>&rarr;</span>
                      <span style={{ color: theme.colors.success }}>{evaluationResult.schemaDefinition.title}</span>
                    </div>
                  )}
                  {evaluationResult.schemaDefinition.description && evaluationResult.schemaDefinition.description !== currentSchema?.description && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing.xs, lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 600, color: theme.colors.muted, minWidth: 80 }}>Description:</span>
                      <span style={{ textDecoration: 'line-through', color: theme.colors.danger }}>{currentSchema?.description || '(empty)'}</span>
                      <span style={{ color: theme.colors.muted }}>&rarr;</span>
                      <span style={{ color: theme.colors.success }}>{evaluationResult.schemaDefinition.description}</span>
                    </div>
                  )}
                  {evaluationResult.schemaDefinition.icon && evaluationResult.schemaDefinition.icon !== currentSchema?.icon && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing.xs, lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 600, color: theme.colors.muted, minWidth: 80 }}>Icon:</span>
                      <span style={{ textDecoration: 'line-through', color: theme.colors.danger }}>{currentSchema?.icon || '(empty)'}</span>
                      <span style={{ color: theme.colors.muted }}>&rarr;</span>
                      <span style={{ color: theme.colors.success }}>{evaluationResult.schemaDefinition.icon}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Field-level diff view */}
              {evaluationResult?.schemaDefinition?.fields && (
                <FieldDiffView
                  originalFields={currentSchema?.fields || []}
                  updatedFields={evaluationResult.schemaDefinition.fields}
                />
              )}

              {/* Collapsible AI Feedback — detail below the summary */}
              {evaluationResult.reasoning && (
                <div style={{ marginBottom: theme.spacing.md }}>
                  <SectionLabel theme={theme}>AI Feedback</SectionLabel>
                  <FeedbackCard theme={theme}>
                    <FeedbackCardToggle
                      theme={theme}
                      onClick={() => setFeedbackExpanded(v => !v)}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {feedbackExpanded ? 'AI summary' : evaluationResult.reasoning.slice(0, 80) + '…'}
                      </span>
                      {feedbackExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </FeedbackCardToggle>
                    {feedbackExpanded && (
                      <FeedbackCardBody theme={theme}>
                        {evaluationResult.reasoning}
                      </FeedbackCardBody>
                    )}
                  </FeedbackCard>
                </div>
              )}

              {/* Refinement Chat */}
              <ChatSection theme={theme}>
                <SectionLabel theme={theme}>Refine with AI</SectionLabel>

                {conversationHistory.length > 0 && (
                  <ChatHistory theme={theme}>
                    {conversationHistory.map((turn, i) => (
                      <ChatBubble key={i} $role={turn.role} theme={theme}>
                        <ChatBubbleLabel>{turn.role === 'user' ? 'You' : 'AI'}</ChatBubbleLabel>
                        {turn.content}
                      </ChatBubble>
                    ))}
                  </ChatHistory>
                )}

                <ChatInputRow theme={theme}>
                  <ChatTextArea
                    theme={theme}
                    value={chatMessage}
                    onChange={e => setChatMessage(e.target.value)}
                    placeholder='e.g. "Make location required" or "Add a signature field"'
                    disabled={isRefining}
                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleRefine(); }}
                  />
                  <Button
                    variant="primary"
                    onClick={handleRefine}
                    disabled={isRefining || !chatMessage.trim()}
                    style={{ alignSelf: 'flex-end' }}
                    title="Ctrl+Enter to send"
                  >
                    {isRefining ? <ButtonSpinner /> : <Send size={14} />}
                  </Button>
                </ChatInputRow>
                <SendHint theme={theme}>Ctrl+Enter to send</SendHint>
              </ChatSection>
            </div>
          )}
        </ModalBody>

        <ModalFooter theme={theme}>
          {!evaluationResult ? (
            <Button variant="secondary" onClick={onSave} disabled={isEvaluating}>
              Save Without Evaluation
            </Button>
          ) : (
            <>
              {hasActionableResult ? (
                <>
                  <Button variant="secondary" onClick={onSave} disabled={isRefining}>
                    Save As-Is
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleApply}
                    disabled={isRefining}
                  >
                    <Eye size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Apply
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleApplyAndSave}
                    disabled={isRefining}
                  >
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Apply &amp; Save
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={onSave}>
                  <Save size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Save
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};
